<template>
  <div class="pa-4">
    <div v-if="loading">
      <v-skeleton-loader type="card"></v-skeleton-loader>
    </div>
    <div v-if="!loading">
      <div v-if="academy">
        <div class="text-center">
          <v-avatar size="250">
            <ImageDisplay :src="academyImage" style="max-width:300px;max-height:300px;" />
          </v-avatar>
          <div class="mb-4"></div>
          <ImageUpload :vm="this" :add-small="false" :success-function="successFunction"
            :label="$t('message.change_image')"></ImageUpload>
        </div>
        <div fill-height class="mt-1">
          <v-card class="pa-2" elevation="0" outlined v-allowed="'root'">
            <v-card-text>
              <v-list two-lines>
                <v-list-item>
                  <v-text-field dense outlined @focus="assignCurrentField('name')" v-on:blur="updateField('name')"
                    :label="$t('message.name')" v-model="academy.name"></v-text-field>
                </v-list-item>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-list-item v-if="academy" v-bind="attrs" v-on="on">
                      <v-text-field dense outlined :label="$t('message.public_page_id')"
                        @focus="assignCurrentField('identifier')" v-on:blur="updateField('identifier')"
                        v-model="academy.identifier"></v-text-field>
                    </v-list-item>
                  </template>
                  <span>{{ $t("message.public_page_id_explained") }}</span>
                </v-tooltip>
                <v-list-item v-if="academy && academy.identifier" style="margin-top:-40px" class="pb-5 clickable">
                  <div @click="copyPublicUrl()">
                    <v-icon v-if="academy.identifier" @click="copyPublicUrl()">mdi-content-copy
                    </v-icon>
                    {{ $t('message.copy') }}
                  </div>
                </v-list-item>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-list-item v-bind="attrs" v-on="on">
                      <v-text-field dense outlined :label="$t('message.short_name')"
                        @focus="assignCurrentField('shortName')" v-on:blur="updateField('shortName')"
                        v-model="academy.shortName"></v-text-field>
                    </v-list-item>
                  </template>
                  <span>{{ $t("message.short_name_explained") }}</span>
                </v-tooltip>
                <v-list-item>
                  <v-list-item-content>
                    <Locations :width="'100%'" :label="$t('message.address')" v-model="geolocatedAddress"
                      :display="academy.address">
                    </Locations>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item v-if="academy && academy.address" style="margin-top:-40px" class="pb-5 clickable">
                  <div @click="copyAddress()">
                    <v-icon v-if="academy.identifier">mdi-content-copy
                    </v-icon>
                    {{ $t('message.copy') }}
                  </div>
                </v-list-item>
                <v-list-item>
                  <v-text-field dense outlined :label="$t('message.email')" @focus="assignCurrentField('email')"
                    v-on:blur="updateField('email')" v-model="academy.email" :rules="emailRules"></v-text-field>
                </v-list-item>
                <v-list-item>
                  <v-text-field dense outlined :label="$t('message.contact')" @focus="assignCurrentField('contact')"
                    v-on:blur="updateField('contact')" v-model="academy.contact"></v-text-field>
                </v-list-item>
                <v-list-item>
                  <v-text-field dense outlined :label="$t('message.phone')" @focus="assignCurrentField('phone')"
                    v-on:blur="updateField('phone')" v-model="academy.phone"></v-text-field>
                </v-list-item>
                <v-list-item>
                  <v-text-field dense outlined :label="$t('message.web_site')" @focus="assignCurrentField('website')"
                    v-on:blur="updateField('website')" v-model="academy.website"></v-text-field>
                </v-list-item>
                <v-list-item>
                  <affiliations :label="$t('message.international_affiliation')" @focus="assignCurrentField('name')"
                    v-model="affiliation" />
                </v-list-item>
                <v-list-item>
                  <local-affiliations :label="$t('message.local_affiliation')" v-model="localAffiliation" />
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </div>
        <div xs12 fill-height class="mt-2">
          <v-card elevation="0" outlined>
            <v-card-title>
              <v-icon class="mr-1">mdi-image</v-icon>
              {{ $t('message.alt_image') }}
            </v-card-title>
            <v-card-text class="supporting-text">
              {{ $t('message.alt_image_academy_explained') }}
            </v-card-text>
            <div xs12 v-if="altAcademyImage" style="overflow-y: hidden; max-height:350px">
              <ImageDisplay :src="altAcademyImage" style="width:100%" />
            </div>
            <CoverImage class="mb-4" :vm="this" :success-function="uploadedAltImage"></CoverImage>
            <v-card-actions style="width:10rem;">
              <v-btn block elevation="0" outlined v-if="altAcademyImage" @click="clearAltImage()" color="error" small>
                <v-icon class="mr-1">mdi-trash-can-outline</v-icon>
                {{ $t('message.delete') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </div>
        <div fill-height class="mt-2">
          <v-card elevation="0" outlined>
            <v-card-title>
              <v-icon class="mr-2 mb-1">mdi-text-box-outline</v-icon>
              {{ $t("message.description") }}
            </v-card-title>
            <v-card-text>
              <v-textarea outlined @focus="assignCurrentField('description')" v-on:blur="updateField('description')"
                v-model="academy.description"></v-textarea>
            </v-card-text>
          </v-card>
        </div>
        <div fill-height class="mt-2">
          <div>
            <v-expansion-panels flat>
              <v-expansion-panel :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'"
                v-allowed="'tablet_credentials'">
                <v-expansion-panel-header>
                  <div class="text-left">
                    <v-icon>mdi-key-outline</v-icon>
                    {{ $t("message.tablet_credentials") }}
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-card-text>
                    <v-text-field dense outlined label="ID" v-model="academy.tabletCredentials.id"></v-text-field>
                    <v-text-field dense outlined :label="$t('message.password')"
                      v-model="academy.tabletCredentials.password">
                    </v-text-field>
                  </v-card-text>
                  <v-card-actions>
                    <v-btn block elevation="0" outlined small color="primary" @click="updateTabletCredentials()">
                      {{
                        $t("message.update")
                      }}
                    </v-btn>
                  </v-card-actions>
                </v-expansion-panel-content>
              </v-expansion-panel>
              <v-expansion-panel :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'" v-if="managers"
                v-allowed="'root'">
                <v-expansion-panel-header>
                  <div class="text-left">
                    <v-icon>mdi-police-badge-outline</v-icon>
                    {{ $t("message.admins") }}
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-card-text>
                    <v-autocomplete v-allowed="'root'" dense outlined v-model="selectedAdmin" :items="members"
                      :label="$t('message.add_new_admin')" item-text="text" item-value="value"
                      clearable></v-autocomplete>
                    <template v-for="(m, index) in managers">
                      <v-list-item :key="m._id + index" class="text-left" v-if="!isAppAdmin(m._id)">
                        <v-list-item-avatar>
                          <ImageDisplay v-if="m.userImage" :src="m.userImage" :title="m.fName + ' ' + m.lName" />
                          <img v-else :src="assets('default-profile-avatar.png')" :title="m.fName + ' ' + m.lName" />
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title>
                            <span v-html="m.displayName"></span>
                          </v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-btn v-allowed="'root'" @click="removeAdmin(m._id)" small icon>
                            <v-icon>mdi-delete-outline</v-icon>
                          </v-btn>
                          <v-btn :loading="loadingPermissions" v-allowed="'root'" small icon
                            @click="viewAdminPermissions(m._id)">
                            <v-icon>mdi-tune-vertical-variant</v-icon>
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item>
                      <v-divider v-if="index + 1 < managers.length" :key="index"></v-divider>
                    </template>
                  </v-card-text>
                </v-expansion-panel-content>
              </v-expansion-panel>
              <v-expansion-panel :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'"
                v-if="stripeReady && academy.configuration && academy.configuration.allowTerminal" v-allowed="'root'">
                <v-expansion-panel-header @click="addStripeTerminal()">
                  <div class="text-left">
                    <v-icon>mdi-point-of-sale</v-icon>
                    {{ $t("message.terminal") }}
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-card-text>
                    <div v-if="terminals && terminals.length > 0">
                      <template v-for="(i, index) in terminals">
                        <v-list-item :key="'terminal_' + index" class="text-left">
                          <v-list-item-content>
                            <v-list-item-title>
                              <span v-html="i.label"></span>
                            </v-list-item-title>
                          </v-list-item-content>
                          <v-list-item-action>
                            <v-btn v-if="i.paired" icon small>
                              <v-icon :color="themeColor('success')">mdi-lan-connect</v-icon>
                            </v-btn>
                            <v-btn v-else :disabled="pairing" icon @click="pairTerminal(i)" small>
                              <v-icon v-if="pairing" class="rotate">mdi-sync</v-icon>
                              <v-icon v-else>mdi-connection</v-icon>
                            </v-btn>
                          </v-list-item-action>
                          <v-list-item-action>
                            <v-btn icon @click="deleteTerminal(i)" small>
                              <v-icon>mdi-trash-can-outline</v-icon>
                            </v-btn>
                          </v-list-item-action>
                        </v-list-item>
                      </template>
                      <div v-if="pairingError" class="error--text">
                        <v-icon class="mr-1" color="error">mdi-alert-circle-outline</v-icon>
                        {{ pairingError }}
                      </div>
                    </div>
                    <div v-else class="text-h6">
                      {{ $t("message.no_data") }}
                    </div>
                  </v-card-text>
                  <v-card-text v-if="!terminals || terminals.length < 1">
                    <v-text-field outlined dense v-model="readerLabel" :label="$t('message.label')"></v-text-field>
                    <v-text-field outlined dense v-model="registrationCode"
                      :label="$t('message.registration_code')"></v-text-field>
                    <v-select v-if="false" outlined dense v-model="readerStripeAccount" :items="altStripeAccounts"
                      item-text="name" item-value="id" clearable :label="$t('message.alt_stripe_account')"></v-select>
                    <v-btn outlined :disabled="!readerLabel || !registrationCode" elevation="0" @click="addTerminal()"
                      color="primary" small>
                      {{ $t('message.add_terminal') }}
                    </v-btn>
                  </v-card-text>
                </v-expansion-panel-content>
              </v-expansion-panel>
              <v-expansion-panel :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'"
                v-allowed="'root'">
                <v-expansion-panel-header>
                  <div class="text-left">
                    <v-icon>mdi-list-box-outline</v-icon>
                    {{ $t("message.contact_form") }}
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-card-text>
                    <v-switch hide-details @change="updateAcademyConfiguration"
                      v-model="academy.configuration.hideContactFormInput"
                      :label="$t('message.hide_message_input')"></v-switch>
                    <v-switch hide-details @change="updateAcademyConfiguration"
                      v-model="academy.configuration.makePhoneOptional"
                      :label="$t('message.make_phone_optional')"></v-switch>
                  </v-card-text>
                  <v-divider></v-divider>
                  <v-card-actions>
                    {{ $t('message.custom_fields') }}
                    <v-spacer></v-spacer>
                    <v-btn outlined elevation="0" color="primary" small @click="addNewExraField()">
                      {{ $t('message.add') }}
                    </v-btn>
                  </v-card-actions>
                  <v-list two-line v-if="contactFormExtraFields.length > 0">
                    <template v-for="(field, index) in contactFormExtraFields">
                      <v-list-item :key="'contact_form_extra_field_' + index">
                        <v-list-item-action>
                          <span class="text-subtitle-1">{{ index + 1 }}</span>
                        </v-list-item-action>
                        <v-list-item-content class="text-left">
                          <v-list-item-title>
                            <v-text-field outlined hide-details dense :label="$t('message.name')"
                              v-model="field.fieldName"></v-text-field>
                          </v-list-item-title>
                          <div class="mt-3">
                            <v-select outlined hide-details dense :label="$t('message.answer_type')"
                              :items="customFieldTypes" v-model="field.fieldType"></v-select>
                          </div>
                          <div class="mt-3" v-if="field.fieldType == 'multiple_choice'">
                            <div class="text-caption mb-2">{{ $t('message.field_options_hint') }}</div>
                            <v-text-field v-for="(o, oIndex) in field.options"
                              :key="'cf_opt_' + index + '_' + oIndex" outlined dense
                              :label="$t('message.answer_option') + ' ' + (oIndex + 1)"
                              v-model="field.options[oIndex]" maxlength="30" counter></v-text-field>
                            <v-btn elevation="0" small @click="addFieldOption(field)">
                              <v-icon size="20">mdi-plus-circle-outline</v-icon>{{ $t('message.add') }}
                            </v-btn>
                          </div>
                          <v-list-item-subtitle class="mt-0 pt-0">
                            <v-checkbox hide-details dense :label="$t('message.required')"
                              v-model="field.required"></v-checkbox>
                          </v-list-item-subtitle>
                          <v-list-item-subtitle class="mt-0 pt-0">
                            <v-checkbox hide-details dense :label="$t('message.show_in_contact_form')"
                              v-model="field.showInContactForm"></v-checkbox>
                          </v-list-item-subtitle>
                          <v-list-item-subtitle class="mt-0 pt-0">
                            <v-checkbox hide-details dense :label="$t('message.show_in_schedule_class')"
                              v-model="field.showInScheduleClassForm"></v-checkbox>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-btn elevation="0" icon small @click="removeExtraField(index)">
                            <v-icon>mdi-trash-can-outline</v-icon>
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item>
                      <v-divider v-if="index + 1 < contactFormExtraFields.length" :key="index"></v-divider>
                    </template>
                  </v-list>
                  <v-card-text class="text-center" v-else>
                    {{ $t('message.add_custom_fields') }}
                  </v-card-text>
                  <v-card-actions>
                    <v-btn block elevation="0" color="primary" outlined small @click="updateContactFormExtraFields()">
                      {{ $t('message.update') }}
                    </v-btn>
                  </v-card-actions>
                </v-expansion-panel-content>
              </v-expansion-panel>
              <v-expansion-panel :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'"
                v-allowed="'root'">
                <v-expansion-panel-header>
                  <div class="text-left">
                    <v-icon>mdi-palette</v-icon>
                    {{ $t("message.academy_colors") }}
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="text-subtitle-1">{{ $t("message.academy_colors_explained") }}</div>
                  <div>
                    <div class="mt-2">
                      <v-btn @click="showColorEditor('primary')" x-small icon :color="colorTheme.primary">
                        <v-icon>mdi-pencil-box-outline</v-icon>
                      </v-btn>
                      <span class="ml-2">{{ $t("message.primary") }}</span>
                    </div>
                    <div class="mt-2" v-show="false">
                      <v-btn @click="showColorEditor('secondary')" x-small icon :color="colorTheme.secondary">
                        <v-icon>mdi-pencil-box-outline</v-icon>
                      </v-btn>
                      <span class="ml-2">{{ $t("message.secondary") }}</span>
                    </div>
                    <div>
                      <v-switch @change="updateColorTheme()" v-model="colorTheme.lightMode"
                        :label="$t('message.light_mode')"></v-switch>
                    </div>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
              <v-expansion-panel :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'"
                v-allowed="'view_updates'">
                <v-expansion-panel-header @click="getUpdates()">
                  <div class="text-left">
                    <v-icon>mdi-server-outline</v-icon>
                    {{ $t("message.admins_activity") }}
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-card-text>
                    <v-autocomplete v-model="activityActionFilter" @change="getUpdates" :items="activityActions"
                      outlined dense clearable :label="$t('message.filter')"></v-autocomplete>
                  </v-card-text>
                  <AcademyActivity v-if="updates && updates.length > 0" :activity="updates"></AcademyActivity>
                  <div v-if="updatesHasMore" class="text-center pb-3">
                    <v-btn outlined small elevation="0" color="primary" :loading="loadingMoreUpdates"
                      @click="loadMoreUpdates()">
                      {{ $t('message.view_more') }}
                    </v-btn>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
              <v-expansion-panel :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'"
                v-allowed="'root'">
                <v-expansion-panel-header @click="getAvailableEmails(); getEmails()">
                  <div class="text-left">
                    <v-icon>mdi-email-fast-outline</v-icon>
                    {{ $t("message.sent_emails") }}
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content v-if="emails">
                  <v-card-text>
                    <v-autocomplete v-if="availableEmails" @input="searchEmail" v-model="selectedEmail"
                      :items="availableEmails" :label="$t('message.search_by_email')" clearable outlined dense>
                    </v-autocomplete>
                    <v-switch :label="$t('message.failed_emails_only')" @change="searchEmail"
                      v-model="failedEmailsFilter"></v-switch>
                  </v-card-text>
                  <div v-if="!selectedEmail && (!emails || emails.length == 0)"
                    class="text-center text-cubtitle-1 primary--text">
                    {{ $t("message.no_emails") }}
                  </div>
                  <v-skeleton-loader v-if="loadingEmails"
                    type="list-item-avatar, divider, list-item-avatar, divider, list-item-avatar"></v-skeleton-loader>
                  <template v-for="(e, index) in emails">
                    <v-list-item two-line :key="'update' + index" class="text-left" @click="getEmail(e)">
                      <v-list-item-content>
                        <v-list-item-title>
                          <span v-if="e.opening">
                            <v-progress-circular size="15" width="5" indeterminate
                              color="secondary"></v-progress-circular>
                          </span>
                          <span v-else>{{ e.subject }}</span>
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ $d(new Date(e.time), "short", $i18n.locale) }}
                          <v-icon class="ml-1" size="20" v-if="e.failed" color="error">mdi-email-alert-outline</v-icon>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-badge color="secondary" :content="e.recipients">
                          <v-icon size="20" v-if="e.recipients == 1">mdi-account-outline</v-icon>
                          <v-icon size="20" v-else-if="e.recipients == 2">mdi-account-multiple-outline</v-icon>
                          <v-icon size="20" v-else>mdi-account-group-outline</v-icon>
                        </v-badge>
                      </v-list-item-action>
                    </v-list-item>
                    <v-divider v-if="index + 1 < emails.length" :key="index"></v-divider>
                  </template>
                  <div class="text-center mt-2" v-if="emailsHasMore">
                    <v-btn small outlined color="primary" :loading="loadingMoreEmails" @click="getEmails(true)">
                      {{ $t("message.view_more") }}
                    </v-btn>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
              <v-expansion-panel :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'"
                v-allowed="'root'">
                <v-expansion-panel-header>
                  <div class="text-left">
                    <v-icon>mdi-email-remove-outline</v-icon>
                    {{ $t("message.unsubscribed_emails") }}
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-layout>
                    <v-text-field outlined v-model="unsubscribedEmail" :label="$t('message.email')"></v-text-field>
                    <v-btn elevation="0" :disabled="!unsubscribedEmail || unsubscribedEmail.indexOf('@') < 0" icon
                      color="primary" small @click="addUnsubscribedEmail()">
                      <v-icon>mdi-plus-circle-outline</v-icon>
                    </v-btn>
                  </v-layout>
                  <template v-for="(e, index) in academy.unsubscribedEmails">
                    <v-list-item two-line :key="'unsubscribed_email' + index" class="text-left"
                      @click="goToUnsubscribedEmailUser(e)">
                      <v-list-item-action>
                        <v-icon v-if="e.user">mdi-account-circle-outline</v-icon>
                        <v-icon v-else>mdi-at</v-icon>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ e.email }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ $t("message.added_on") }}: {{ $d(new Date(e.addedOn), "short", $i18n.locale) }}
                        </v-list-item-subtitle>
                        <v-list-item-subtitle v-if="e.publicForm">
                          {{ $t("message.public_form_submission") }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-btn icon @click.stop="removeUnsubscribedEmail(e.email)">
                          <v-icon>mdi-close-circle-outline</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                    <v-divider v-if="index + 1 < academy.unsubscribedEmails" :key="index"></v-divider>
                  </template>
                </v-expansion-panel-content>
              </v-expansion-panel>
              <v-expansion-panel :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'"
                v-allowed="'root'">
                <v-expansion-panel-header @click="getEmailTemplates()">
                  <div class="text-left">
                    <v-icon>mdi-email-sync-outline</v-icon>
                    {{ $t("message.automated_emails") }}
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content v-if="academy.automatedEmails">
                  <div>
                    <div class="text-subtitle-1">
                      {{ $t("message.daily_membership_alerts") }}
                      <div class="text-caption">{{ $t("message.daily_membership_alerts_explained") }}</div>
                      <v-switch :label="$t('message.active')" dense :hide-details="true" class="ml-1"
                        v-model="academy.automatedEmails['dailyMembershipAlerts'].active"></v-switch>
                    </div>
                    <div>
                      <div>
                        <span class="mr-1 text-subtitle-2">{{ $t("message.alternative_emails") }}:</span>
                        <template v-for="(i, index) in academy.automatedEmails['dailyMembershipAlerts'].emails">
                          <v-chip :close="true" @click:close="removeAlternativeEmail('dailyMembershipAlerts', i)"
                            :key="'alt_automation_email' + index" v-if="i && i != ''" small class="ma-1">{{ i
                            }}</v-chip>
                        </template>
                      </div>
                    </div>
                    <v-card-actions class="text-subtitle-1">
                      <v-text-field :hide-details="true" outlined dense class="ml-2"
                        :label="$t('message.add_alternative_email')"
                        v-model="newAlternativeEmails['dailyMembershipAlerts']"></v-text-field>
                      <v-btn class="ml-1" small icon @click="addAlternativeEmail('dailyMembershipAlerts')">
                        <v-icon>mdi-plus-circle-outline</v-icon>
                      </v-btn>
                    </v-card-actions>
                  </div>
                  <v-divider></v-divider>
                  <div v-if="academy.automatedEmails['dailyMembershipAlerts'].active">
                    <div class="text-subtitle-1">
                      {{ $t("message.weekly_members_update") }}
                      <div class="text-caption">{{ $t("message.weekly_members_update_explained") }}</div>
                      <v-switch :label="$t('message.active')" dense :hide-details="true" class="ml-1"
                        v-model="academy.automatedEmails['weeklyMembersUpdate'].active"></v-switch>
                    </div>
                    <div>
                      <div>
                        <span class="mr-1 text-subtitle-2">{{ $t("message.alternative_emails") }}:</span>
                        <template v-for="(i, index) in academy.automatedEmails['weeklyMembersUpdate'].emails">
                          <v-chip close close-icon="mdi-delete"
                            @click:close="removeAutomatedEmail('weeklyMembersUpdate', i)"
                            :key="'alt_automation_email' + index" v-if="i && i != ''" small class="ma-1">{{ i
                            }}</v-chip>
                        </template>
                      </div>
                    </div>
                    <v-card-actions class="text-subtitle-1">
                      <v-text-field :hide-details="true" outlined dense class="ml-2"
                        :label="$t('message.add_alternative_email')"
                        v-model="newAlternativeEmails['weeklyMembersUpdate']"></v-text-field>
                      <v-btn class="ml-1" small icon @click="addAlternativeEmail('weeklyMembersUpdate')">
                        <v-icon>mdi-plus-circle-outline</v-icon>
                      </v-btn>
                    </v-card-actions>
                    <v-divider></v-divider>
                  </div>
                  <div>
                    <div class="text-subtitle-1">
                      {{ $t("message.purchase_confirmations") }}
                      <div class="text-caption">{{ $t("message.purchase_confirmations_explained") }}</div>
                      <v-switch :label="$t('message.active')" dense :hide-details="true" class="ml-1"
                        v-model="academy.automatedEmails['purchaseConfirmations'].active"></v-switch>
                    </div>
                    <div>
                      <div>
                        <span class="mr-1 text-subtitle-2">{{ $t("message.alternative_emails") }}:</span>
                        <template v-for="(i, index) in academy.automatedEmails['purchaseConfirmations'].emails">
                          <v-chip close close-icon="mdi-delete"
                            @click:close="removeAutomatedEmail('purchaseConfirmations', i)"
                            :key="'alt_automation_email' + index" v-if="i && i != ''" small class="ma-1">{{ i
                            }}</v-chip>
                        </template>
                      </div>
                    </div>
                    <v-card-actions class="text-subtitle-1">
                      <v-text-field :hide-details="true" outlined dense class="ml-2"
                        :label="$t('message.add_alternative_email')"
                        v-model="newAlternativeEmails['purchaseConfirmations']"></v-text-field>
                      <v-btn class="ml-1" small icon @click="addAlternativeEmail('purchaseConfirmations')">
                        <v-icon>mdi-plus-circle-outline</v-icon>
                      </v-btn>
                    </v-card-actions>
                    <v-card-actions v-show="false">
                      <v-select item-text="name" item-value="_id" outlined dense :clearable="true"
                        v-model="academy.automatedEmails['purchaseConfirmations'].template"
                        :label="$t('message.select_email_template')" :items="emailTemplates">
                      </v-select>
                    </v-card-actions>
                  </div>
                  <v-divider></v-divider>
                  <div>
                    <div class="text-subtitle-1">
                      {{ $t("message.new_visit_submissions") }}
                      <div class="text-caption">{{ $t("message.new_visit_submissions_explained") }}</div>
                    </div>
                    <div>
                      <div>
                        <span class="mr-1 text-subtitle-2">{{ $t("message.alternative_emails") }}:</span>
                        <template v-for="(i, index) in academy.automatedEmails['newVisitSubmissions'].emails">
                          <v-chip @click:close="removeAutomatedEmail('newVisitSubmissions', i)" close
                            close-icon="mdi-delete" :key="'alt_automation_email' + index" v-if="i && i != ''" small
                            class="ma-1">{{ i
                            }}</v-chip>
                        </template>
                      </div>
                    </div>
                    <v-card-actions class="text-subtitle-1">
                      <v-text-field :hide-details="true" outlined dense class="ml-2"
                        :label="$t('message.add_alternative_email')"
                        v-model="newAlternativeEmails['newVisitSubmissions']"></v-text-field>
                      <v-btn class="ml-1" small icon @click="addAlternativeEmail('newVisitSubmissions')">
                        <v-icon>mdi-plus-circle-outline</v-icon>
                      </v-btn>
                    </v-card-actions>
                    <v-card-actions>
                      <v-select item-text="name" item-value="_id" outlined dense :clearable="true"
                        v-model="academy.automatedEmails['newVisitSubmissions'].template"
                        :label="$t('message.select_email_template')" :items="emailTemplates">
                      </v-select>
                    </v-card-actions>
                  </div>
                  <v-divider></v-divider>
                  <div>
                    <div class="text-subtitle-1">
                      {{ $t("message.inquiry_submissions") }}
                      <div class="text-caption">{{ $t("message.inquiry_submissions_explained") }}</div>
                    </div>
                    <div>
                      <div>
                        <span class="mr-1 text-subtitle-2">{{ $t("message.alternative_emails") }}:</span>
                        <template v-for="(i, index) in academy.automatedEmails['inquirySubmissions'].emails">
                          <v-chip close close-icon="mdi-delete"
                            @click:close="removeAutomatedEmail('inquirySubmissions', i)"
                            :key="'alt_automation_email' + index" v-if="i && i != ''" small class="ma-1">{{ i
                            }}</v-chip>
                        </template>
                      </div>
                    </div>
                    <v-card-actions class="text-subtitle-1">
                      <v-text-field :hide-details="true" outlined dense class="ml-2"
                        :label="$t('message.add_alternative_email')"
                        v-model="newAlternativeEmails['inquirySubmissions']"></v-text-field>
                      <v-btn class="ml-1" small icon @click="addAlternativeEmail('inquirySubmissions')">
                        <v-icon>mdi-plus-circle-outline</v-icon>
                      </v-btn>
                    </v-card-actions>
                    <v-card-actions>
                      <v-select item-text="name" item-value="_id" outlined dense :clearable="true"
                        v-model="academy.automatedEmails['inquirySubmissions'].template"
                        :label="$t('message.select_email_template')" :items="emailTemplates">
                      </v-select>
                    </v-card-actions>
                  </div>
                  <v-divider></v-divider>
                  <div v-if="academy.automatedEmails['birthdayCelebration']">
                    <div class="text-subtitle-1">
                      {{ $t("message.birthday_celebration") }}
                      <div class="text-caption">{{ $t("message.birthday_celebration_explained") }}</div>
                      <v-switch :label="$t('message.active')" dense :hide-details="true" class="ml-1"
                        v-model="academy.automatedEmails['birthdayCelebration'].active"></v-switch>
                    </div>
                    <v-card-actions>
                      <v-select item-text="name" item-value="_id" outlined dense :clearable="true"
                        v-model="academy.automatedEmails['birthdayCelebration'].template"
                        :label="$t('message.select_email_template')" :items="emailTemplates">
                      </v-select>
                    </v-card-actions>
                  </div>
                  <v-divider></v-divider>
                  <div v-if="academy.automatedEmails['expiringSoon']">
                    <div class="text-subtitle-1">
                      {{ $t("message.expiring_soon_template") }}
                      <div class="text-caption">{{ $t("message.expiring_soon_template_explained") }}</div>
                    </div>
                    <v-card-actions>
                      <v-select item-text="name" item-value="_id" outlined dense :clearable="true"
                        v-model="academy.automatedEmails['expiringSoon'].template"
                        :label="$t('message.select_email_template')" :items="emailTemplates">
                      </v-select>
                    </v-card-actions>
                  </div>
                  <v-divider></v-divider>
                  <div v-if="academy.automatedEmails['dailyMembershipAlerts'].active">
                    <div class="text-subtitle-1">
                      {{ $t("message.payment_reminders") }}
                      <div class="text-caption">{{ $t("message.payment_reminders_explained") }}</div>
                      <v-switch :label="$t('message.active')" dense :hide-details="true" class="ml-1"
                        v-model="academy.automatedEmails['paymentReminders'].active"></v-switch>
                    </div>
                    <v-divider></v-divider>
                  </div>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn outlined @click="updateAlternateEmails()" small color="primary" elevation="0">{{
                      $t("message.update")
                    }}</v-btn>
                  </v-card-actions>
                </v-expansion-panel-content>
              </v-expansion-panel>
              <v-expansion-panel v-if="false" :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'"
                v-allowed="'root'">
                <v-expansion-panel-header>
                  <div class="text-left">
                    <v-icon>mdi-email-edit-outline</v-icon>
                    {{ $t("message.alternative_email_server") }}
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-switch class="ml-2" @change="updateEmailServer" v-model="hasEmailServer"></v-switch>
                  <div v-if="hasEmailServer && academy.emailServer">
                    <v-card-text>
                      <div class="text-caption gray--text">
                        {{ $t("message.alternative_email_server_explained") }}
                      </div>
                      <v-select :label="$t('message.email_service')" :items="emailServices"
                        v-model="academy.emailServer.service"></v-select>
                      <v-text-field label="Email" v-model="academy.emailServer.email"></v-text-field>
                      <v-text-field :label="$t('message.password')"
                        v-model="academy.emailServer.password"></v-text-field>
                    </v-card-text>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn elevation="0" small outlined color="primary" @click="updateEmailServer()">
                        {{
                          $t("message.update")
                        }}
                      </v-btn>
                    </v-card-actions>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
              <v-expansion-panel v-if="false && !isSmallerDisplay() && academy.stripeAccountId"
                :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'" v-allowed="'root'">
                <v-expansion-panel-header>
                  <div class="text-left">
                    <v-icon>mdi-credit-card-check-outline</v-icon>
                    {{ $t("message.alternative_stripe_accounts") }}
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div>
                    <v-card-text v-if="academy.altStripeAccounts && academy.altStripeAccounts.length > 0">
                      <template v-for="(i, index) in academy.altStripeAccounts">
                        <v-list-item two-line :key="'alt_stripe_account_' + index" class="text-left">
                          <v-list-item-content>
                            <v-list-item-title>
                              {{ i.name }}
                            </v-list-item-title>
                            <v-list-item-subtitle>
                              {{ i.id }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                        <v-divider v-if="index + 1 < academy.unsubscribedEmails" :key="index"></v-divider>
                      </template>
                    </v-card-text>
                    <v-card-text v-else>
                      {{ $t("message.no_data") }}
                    </v-card-text>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn elevation="0" small color="primary" @click="altStripeAccountDialog = true">
                        {{ $t("message.connect_new") }}
                      </v-btn>
                    </v-card-actions>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
              <v-expansion-panel v-if="academy.configuration"
                :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'" v-allowed="'root'">
                <v-expansion-panel-header>
                  <div class="text-left">
                    <v-icon>mdi-toggle-switch-off-outline</v-icon>
                    {{ $t("message.configuration") }}
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-list>
                    <v-subheader>{{ $t("message.settings_category_kiosk") }}</v-subheader>
                    <v-list-item three-line class="text-left">
                      <v-list-item-action>
                        <v-switch @change="updateAcademyConfiguration"
                          v-model="academy.configuration.altTabletCheckin"></v-switch>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $t('message.enable_alt_tablet_checkin') }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ $t('message.enable_alt_tablet_checkin_explained') }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item two-line class="text-left">
                      <v-list-item-action>
                        <v-switch @change="updateAcademyConfiguration"
                          v-model="academy.configuration.forceClassSelectForTabletCheckins"></v-switch>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $t('message.enable_force_class_select_tablet_checkins') }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ $t('message.enable_force_class_select_tablet_checkins_explained') }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item two-line class="text-left">
                      <v-list-item-action>
                        <v-switch @change="updateAcademyConfiguration"
                          v-model="academy.configuration.tabletCheckinConfirm"></v-switch>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $t('message.enable_tablet_checkin_confirm') }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ $t('message.enable_tablet_checkin_confirm_explained') }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item three-line class="text-left">
                      <v-list-item-action>
                        <v-switch @change="updateAcademyConfiguration"
                          v-model="academy.configuration.displayMembershipStatus"></v-switch>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $t('message.display_membership_status') }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ $t('message.display_membership_status_explained') }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-subheader>{{ $t("message.settings_category_connect") }}</v-subheader>
                    <v-list-item two-line class="text-left">
                      <v-list-item-action>
                        <v-switch @change="updateAcademyConfiguration"
                          v-model="academy.configuration.disableSocialPodium"></v-switch>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $t('message.disable_social_podium') }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ $t('message.disable_social_podium_explained') }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item two-line class="text-left">
                      <v-list-item-action>
                        <v-switch @change="updateAcademyConfiguration"
                          v-model="academy.configuration.blockVideosInConnect"></v-switch>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $t('message.block_videos_connect') }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ $t('message.block_videos_connect_explained') }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item two-line class="text-left">
                      <v-list-item-action>
                        <v-switch @change="updateAcademyConfiguration"
                          v-model="academy.configuration.hidePauseRequestButton"></v-switch>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $t('message.hide_pause_request_connect') }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ $t('message.hide_pause_request_explained') }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item two-line class="text-left">
                      <v-list-item-action>
                        <v-switch @change="updateAcademyConfiguration"
                          v-model="academy.configuration.hideCancellationRequestButton"></v-switch>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $t('message.hide_cancellation_request_connect') }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ $t('message.hide_cancellation_request_explained') }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item two-line class="text-left">
                      <v-list-item-action>
                        <v-switch @change="updateAcademyConfiguration"
                          v-model="academy.configuration.hideMembersInSocial"></v-switch>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $t('message.hide_members_connect') }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ $t('message.hide_members_connect_explained') }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-subheader>{{ $t("message.settings_category_general") }}</v-subheader>
                    <v-list-item three-line class="text-left">
                      <v-list-item-action>
                        <v-switch @change="updateAcademyConfiguration"
                          v-model="academy.configuration.blockMissingDocCheckins"></v-switch>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $t('message.block_missing_docs_checkins') }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ $t('message.block_missing_docs_checkins_explained') }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item two-line class="text-left">
                      <v-list-item-action>
                        <v-switch @change="updateAcademyConfiguration"
                          v-model="academy.configuration.restrictClassReservation"></v-switch>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $t('message.restrict_class_reservation') }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ $t('message.restrict_class_reservation_explained') }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item two-line class="text-left">
                      <v-list-item-action>
                        <v-switch @change="updateAcademyConfiguration"
                          v-model="academy.configuration.useSyllabusRanks"></v-switch>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $t('message.use_syllabus_ranks') }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ $t('message.use_syllabus_ranks_explained') }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item three-line class="text-left">
                      <v-list-item-action>
                        <v-switch @change="updateAcademyConfiguration"
                          v-model="academy.configuration.hideCurriculumProgressInMembersList"></v-switch>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $t('message.hide_curriculum_progress_in_members_list') }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ $t('message.hide_curriculum_progress_in_members_list_explained') }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item two-line class="text-left">
                      <v-list-item-action>
                        <v-switch @change="updateAcademyConfiguration"
                          v-model="academy.configuration.enableExpiredMembershipAlerts"></v-switch>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $t('message.enable_expired_membership_alerts') }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ $t('message.enable_expired_membership_alerts_explained') }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item two-line class="text-left" v-show="false">
                      <v-list-item-action>
                        <v-switch @change="updateAcademyConfiguration"
                          v-model="academy.configuration.privateAcademyFeed"></v-switch>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $t('message.enable_private_academy_wall') }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ $t('message.enable_private_academy_wall_explained') }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item three-line class="text-left" v-show="paymentsReady">
                      <v-list-item-action>
                        <v-switch @change="updateAcademyConfiguration"
                          v-model="academy.configuration.disableSubscriptionAlerts"></v-switch>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $t('message.disable_subscription_alerts') }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ $t('message.disable_subscription_alerts_explained') }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item three-line class="text-left">
                      <v-list-item-action>
                        <v-switch @change="updateAcademyConfiguration"
                          v-model="academy.configuration.disableMissingDocumentAlerts"></v-switch>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $t('message.disable_missing_document_alerts') }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ $t('message.disable_missing_document_alerts_explained') }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item three-line class="text-left"
                      v-show="paymentsReady && academy.configuration.automaticTaxCalculation">
                      <v-list-item-action>
                        <v-switch :disabled="true" v-model="academy.configuration.automaticTaxCalculation"></v-switch>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $t('message.automatic_tax_calculation') }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ $t('message.automatic_tax_calculation_explained') }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item two-line class="text-left" v-show="false">
                      <v-list-item-action>
                        <v-switch @change="updateAcademyConfiguration"
                          v-model="academy.configuration.enableProfileImageAlerts"></v-switch>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $t('message.enable_profile_image_alerts') }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item two-line class="text-left" v-show="false">
                      <v-list-item-action>
                        <v-switch @change="updateAcademyConfiguration"
                          v-model="academy.configuration.enableContractAlerts"></v-switch>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $t('message.enable_contract_alerts') }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item two-line class="text-left" v-show="false">
                      <v-list-item-action>
                        <v-switch @change="updateAcademyConfiguration"
                          v-model="academy.configuration.enableMembershipAlerts"></v-switch>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $t('message.enable_membership_alerts') }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item two-line class="text-left" v-show="false">
                      <v-list-item-action>
                        <v-switch @change="updateAcademyConfiguration"
                          v-model="academy.configuration.enableDocumentAlerts"></v-switch>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $t('message.enable_docuemnt_alerts') }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-subheader>{{ $t("message.values") }}</v-subheader>
                    <v-list-item two-line class="text-left">
                      <v-list-item-action>
                        <v-text-field clearable @change="updateAcademyConfiguration" dense hide-details outlined
                          v-model="academy.configuration.membershipsRemindersInterval"></v-text-field>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $t('message.memberships_email_interval') }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ $t('message.memberships_email_interval_explained') }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>
      </div>
    </div>
    <alert-message :text="updateMessage" />
    <v-dialog v-model="colorEditorDialog" width="80%">
      <v-card outlined>
        <v-card-title>
          {{ $t("message.academy_colors") }}
        </v-card-title>
        <v-card-text>
          <v-color-picker v-if="colorTheme && colorTheme.currentEditing" v-model="colorTheme[colorTheme.currentEditing]"
            dot-size="25" swatches-max-height="200"></v-color-picker>
        </v-card-text>
        <v-card-actions>
          <v-btn outlined elevation="0" @click="updateColorTheme()" small color="primary">
            {{ $t('message.update') }}
          </v-btn>
          <v-btn elevation="0" small @click="colorEditorDialog = null">
            {{ $t('message.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="emailDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card outlined>
        <v-toolbar dense flat class="opposite--text" color="primary">
          <v-spacer></v-spacer>
          <v-btn icon small @click="
            emailDialog = false;
          email = null;
          ">
            <v-icon color="opposite">mdi-close-circle-outline</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card v-if="email" class="pa-2">
          <v-card-actions style="color:gray">
            <v-avatar size="20">
              <ImageDisplay v-if="email.sender && email.sender.userImageSmall" :src="email.sender.userImageSmall" />
              <img v-else :src="assets('default-profile-avatar.png')" />
            </v-avatar>
            <span v-if="email.sender" class="ml-1">
              {{ email.sender.displayName }}
            </span>
            <v-spacer></v-spacer>
            {{ $d(new Date(email.time), "short", $i18n.locale) }}
          </v-card-actions>
          <v-card-title v-if="email.error" class="text-caption error--text">
            {{ email.error }}
          </v-card-title>
          <v-card class="pa-2" outlined>
            <v-card-title>
              <span class="text-subtitle-1">{{ $t("message.recipients") }}:</span>
              <template v-for="(r, index) in email.recipients">
                <v-chip :key="'recipient' + index" v-if="r && r != ''" small class="ma-1">{{ r }}</v-chip>
              </template>
            </v-card-title>
            <v-card-title>
              <span class="text-subtitle-1">{{ $t("message.subject") }}:</span>
              <span style="margin-left:5px">{{ email.subject }}</span>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text v-html="email.contents"></v-card-text>
          </v-card>
          <v-card outlined class="mt-1" v-if="email.openedBy && email.openedBy.length">
            <v-card-title>
              <span class="text-subtitle-1">{{ $t("message.opened_by") }}:</span>
            </v-card-title>
            <v-list>
              <v-list-item dense v-for="i in email.openedBy" :key="'opened_email_' + i._id" @click="doNothing()">
                <v-list-item-content>
                  <v-list-item-title>
                    {{ i.user ? i.user.email : i.email }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ $d(new Date(i.time), "long", $i18n.locale) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-card>
      </v-card>
    </v-dialog>
    <v-dialog v-model="altStripeAccountDialog" max-width="80%">
      <v-card class="pa-2" outlined>
        <v-card-title>
          {{ $t("message.connect_new_stripe_account") }}
        </v-card-title>
        <v-card-text>
          <v-text-field dense outlined v-model="newStripe.email" label="Email"></v-text-field>
          <div class="text-caption">{{ $t('message.connect_new_stripe_account_explained') }}</div>
        </v-card-text>
        <v-card-actions>
          <v-btn :disabled="!newStripe.email" small elevation="0" color="primary"
            @click.stop="connectNewStripeAccount()">
            {{ $t("message.connect_new") }}
          </v-btn>
          <v-btn small elevation="0" class="outlined-btn" @click.stop="altStripeAccountDialog = false">{{
            $t("message.close")
            }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="permissionsDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar flat class="opposite--text" color="primary" dense>
          <v-toolbar-title>
            {{ $t('message.admin_permissions') }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn color="opposite" mall icon @click="permissionsDialog = false">
            <v-icon>mdi-close-circle-outline</v-icon>
          </v-btn>
        </v-toolbar>
        <div class="pa-1">
          <v-alert type="warning" class="text-caption pa-1">{{ $t('message.no_permissions_explained') }}</v-alert>
        </div>
        <v-card-text>
          <v-text-field hide-details dense outlined clearable v-model="permissionsFilter"
            :label="$t('message.filter')"></v-text-field>
        </v-card-text>
        <v-list v-if="selectedPermissions" dense>
          <v-list-item dense v-for="(i, index) in visiblePermissions" :key="'filtered_permission_' + index">
            <v-list-item-content>
              <v-list-item-title>
                <v-switch hide-details @change="updateAdminPermissions" v-model="selectedPermissions[i.field]"
                  :label="i.text"></v-switch>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import AcademyActivity from "@/components/displays/AcademyActivity.vue";
import AlertMessage from "@/components/displays/AlertMessage.vue";
import Affiliations from "@/components/inputs/AffiliationsCombo.vue";
import CoverImage from "@/components/inputs/CoverImageUpload.vue";
import ImageUpload from "@/components/inputs/ImageUploadCroppie.vue";
import LocalAffiliations from "@/components/inputs/LocalAffiliationsCombo.vue";
import Locations from "@/components/inputs/LocationsAutocomplete.vue";
import Api from "@/services/Api";
import Api2 from "@/services/Api2";
import { Environment } from "@/services/Environment";

export default {

  components: {
    Affiliations,
    LocalAffiliations,
    AlertMessage,
    CoverImage,
    AcademyActivity,
    ImageUpload,
    Locations
  },
  data() {
    return {
      stripe_client_id: Environment.get("VUE_APP_STRIPE_CLIENT_ID"),
      scope: this.$route.query.scope,
      code: this.$route.query.code,
      stripe_state: null,
      loading: false,
      academy: null,
      currField: null,
      affiliation: null,
      selectedAdmin: null,
      paymentsReady: false,
      geolocatedAddress: null,
      members: [],
      managers: [],
      localAffiliation: null,
      altStripeAccountDialog: null,
      showUpdateMessage: false,
      failedEmailsFilter: false,
      updateMessage: null,
      updates: null,
      emailDialog: false,
      loadingEmails: false,
      pairingError: null,
      stripeReady: false,
      emails: [],
      emailsHasMore: false,
      loadingMoreEmails: false,
      emailServices: [{ text: 'Google Mail', value: 'gmail' }, { text: 'Outlook/Hotmail/Live', value: 'outlook' }],
      availableEmails: [],
      selectedEmail: null,
      contactFormExtraFields: [],
      //Same vocabulary as the answer types on survey questions.
      customFieldTypes: [
        { text: this.$t('message.text'), value: 'text' },
        { text: this.$t('message.textarea'), value: 'textarea' },
        { text: this.$t('message.true_false'), value: 'boolean' },
        { text: this.$t('message.multiple_choice'), value: 'multiple_choice' },
      ],
      allPermissions: [],
      filteredPermissions: [],
      email: null,
      location: null,
      loadingPermissions: false,
      permissionsFilter: null,
      activityActions: null,
      updatesSkip: 0,
      updatesHasMore: false,
      loadingMoreUpdates: false,
      permissionsDialog: null,
      colorEditorDialog: null,
      selectedPermissions: null,
      unsubscribedEmail: null,
      altAcademyImage: null,
      uploadedAltImageFile: null,
      uploading: false,
      activityActionFilter: null,
      hasEmailServer: false,
      allEmails: null,
      readerLabel: null,
      registrationCode: null,
      terminals: [],
      emailTemplates: [],
      pairing: false,
      newStripe: {},
      altStripeAccounts: null,
      readerStripeAccount: null,
      selectedTerminal: null,
      altImageHeight: 'height:350px;',
      colorTheme: {
        primary: this.themeColor('primary'),
        secondary: this.themeColor('secondary'),
        lightMode: true
      },
      validPermissions: [
        "root",
        "approve_member",
        "reject_member",
        "create_waiver",
        "tablet_credentials",
        "view_updates",
        "edit_schedule",
        "edit_calendar",
        "add_products",
        "edit_products",
        "sell_items",
        "view_purchases",
        "view_subscriptions",
        "view_income",
        "issue_refund",
        "edit_subscription",
        "create_event"
      ],
      academyImage: "/logo-circle.png",
      successFunction: function (uploadedFileData, vm) {
        if (uploadedFileData && vm) {
          vm.academyImage = uploadedFileData.original;
          vm.academy.image = uploadedFileData.location;
          vm.updateField('image', vm.academy.image);
        }
      },
      uploadedAltImage(uploadedFile, vm) {
        if (vm && uploadedFile) {
          vm.altAcademyImage = uploadedFile;
          vm.uploading = false;
          vm.updateField('altImage', uploadedFile);
        }
      },
      newAlternativeEmails: {},
    };
  },
  created() {
    Api2()
      .get("admins/academy/" + this.$store.state.managedAcademy + "/admins")
      .then((response) => {
        this.managers = response.data;
      });
    Api2()
      .get("admins/academy/" + this.$store.state.selectedAcademy + "?populate=unsubscribed_emails")
      .then((response) => {
        let tmpAcademy = response.data;
        if (tmpAcademy.emailServer) {
          this.hasEmailServer = true;
        }
        this.paymentsReady = tmpAcademy.paymentsReady;
        if (tmpAcademy.contactFormExtraFields)
          this.contactFormExtraFields = tmpAcademy.contactFormExtraFields.map((f) => {
            if (!f.fieldType) f.fieldType = 'text';
            if (!f.options) f.options = [];
            return f;
          });
        if (!tmpAcademy.configuration)
          tmpAcademy.configuration = {};
        if (typeof tmpAcademy.configuration.enableProfileImageAlerts == 'undefined')
          tmpAcademy.configuration.enableProfileImageAlerts = true;
        if (typeof tmpAcademy.configuration.enableDocumentAlerts == 'undefined')
          tmpAcademy.configuration.enableDocumentAlerts = true;
        if (typeof tmpAcademy.configuration.enableContractAlerts == 'undefined')
          tmpAcademy.configuration.enableContractAlerts = true;
        if (typeof tmpAcademy.configuration.enableMembershipAlerts == 'undefined')
          tmpAcademy.configuration.enableMembershipAlerts = true;
        if (typeof tmpAcademy.configuration.disableSocialPodium == 'undefined')
          tmpAcademy.configuration.disableSocialPodium = false;
        if (typeof tmpAcademy.configuration.disableSubscriptionAlerts == 'undefined')
          tmpAcademy.configuration.disableSubscriptionAlerts = false;
        if (typeof tmpAcademy.configuration.automaticTaxCalculation == 'undefined')
          tmpAcademy.configuration.automaticTaxCalculation = false;
        if (typeof tmpAcademy.configuration.restrictClassReservation == 'undefined')
          tmpAcademy.configuration.restrictClassReservation = false;
        if (typeof tmpAcademy.configuration.altTabletCheckin == 'undefined')
          tmpAcademy.configuration.altTabletCheckin = false;
        if (typeof tmpAcademy.configuration.hideCurriculumProgressInMembersList == 'undefined')
          tmpAcademy.configuration.hideCurriculumProgressInMembersList = false;
        if (typeof tmpAcademy.configuration.tabletCheckinConfirm == 'undefined')
          tmpAcademy.configuration.tabletCheckinConfirm = false;
        if (typeof tmpAcademy.configuration.forceClassSelectForTabletCheckins == 'undefined')
          tmpAcademy.configuration.forceClassSelectForTabletCheckins = false;
        if (typeof tmpAcademy.configuration.privateAcademyFeed == 'undefined')
          tmpAcademy.configuration.privateAcademyFeed = true;
        if (typeof tmpAcademy.configuration.displayMembershipStatus == 'undefined')
          tmpAcademy.configuration.displayMembershipStatus = false;
        if (tmpAcademy.automatedEmails && !tmpAcademy.automatedEmails.birthdayCelebration)
          tmpAcademy.automatedEmails.birthdayCelebration = { active: false, template: null };
        if (tmpAcademy.automatedEmails && !tmpAcademy.automatedEmails.expiringSoon)
          tmpAcademy.automatedEmails.expiringSoon = { template: null };
        if (tmpAcademy.colorTheme)
          this.colorTheme = tmpAcademy.colorTheme;
        this.localAffiliation = tmpAcademy.localAffiliation;
        this.affiliation = tmpAcademy.affiliation;
        this.academyImage = tmpAcademy.image;
        this.altAcademyImage = tmpAcademy.altImage;
        Api2()
          .get(
            "admins/academy/" + this.$store.state.selectedAcademy + "/publicid"
          )
          .then((response) => {
            tmpAcademy.identifier = response.data.identifier;
            this.academy = tmpAcademy;
          })
          .catch((e) => {
            if (e) this.academy = tmpAcademy;
          });
      })
      .catch((e) => {
        this.loading = false;
        if (e)
          this.showAlert("error", this.$t("message.error_generic"));
      });
    Api2()
      .get("admins/academy/" + this.$store.state.selectedAcademy + "/members")
      .then((response) => {
        const data = response.data;
        if (data) {
          for (const m of data.approved) {
            this.members.push({ value: m._id, text: m.fName + " " + m.lName });
          }
        }
      });
    Api2()
      .get(
        "admins/academy/" +
        this.$store.state.managedAcademy +
        "/is_connected/stripe"
      )
      .then((response) => {
        this.stripeReady = response.data.stripeConnected;
        this.stripe_state = response.data.stripeState;
        if (this.stripeReady)
          this.getTerminals();
      });
    this.getAcvitiyActions();
    if (this.isSmallerDisplay())
      this.altImageHeight = 'height:150px;'

    this.allPermissions = [
      { field: 'root', text: this.$t('message.permission_root') },
      { field: 'approve_member', text: this.$t('message.permission_approve_member') },
      { field: 'view_attendance', text: this.$t('message.permission_view_attendance') },
      { field: 'reject_member', text: this.$t('message.permission_reject_member') },
      { field: 'create_waiver', text: this.$t('message.permission_create_waiver') },
      { field: 'send_waiver', text: this.$t('message.permission_send_waiver') },
      { field: 'manage_documents', text: this.$t('message.permission_manage_documents') },
      { field: 'tablet_credentials', text: this.$t('message.permission_tablet_credentials') },
      { field: 'view_updates', text: this.$t('message.permission_view_updates') },
      { field: 'edit_schedule', text: this.$t('message.permission_edit_schedule') },
      { field: 'edit_calendar', text: this.$t('message.permission_edit_calendar') },
      { field: 'manage_event', text: this.$t('message.permission_manage_event') },
      { field: 'view_products', text: this.$t('message.permission_view_products') },
      { field: 'add_products', text: this.$t('message.permission_add_products') },
      { field: 'edit_products', text: this.$t('message.permission_edit_products') },
      { field: 'sell_items', text: this.$t('message.permission_sell_items') },
      { field: 'view_purchases', text: this.$t('message.permission_view_purchases') },
      { field: 'view_payment_alerts', text: this.$t('message.permission_view_payment_alerts') },
      { field: 'view_subscriptions', text: this.$t('message.permission_view_subscriptions') },
      { field: 'view_income', text: this.$t('message.permission_view_income') },
      { field: 'issue_refund', text: this.$t('message.permission_issue_refund') },
      { field: 'edit_subscription', text: this.$t('message.permission_edit_subscription') },
      { field: 'manage_syllabus', text: this.$t('message.permission_manage_syllabus') },
      { field: 'manage_syllabus_instances', text: this.$t('message.permission_syllabus_instance_data') },
      { field: 'permission_social_wall', text: this.$t('message.permission_social_wall') },
      { field: 'view_members_data', text: this.$t('message.view_members_data') },
      { field: 'view_growth_data', text: this.$t('message.view_growth_data') },
      { field: 'manage_flowcharts_data', text: this.$t('message.permission_manage_flowcharts_data') },
      { field: 'manage_videos_data', text: this.$t('message.permission_manage_videos_data') },
      { field: 'view_growth_data', text: this.$t('message.view_growth_data') },
      { field: 'get_system_mobile_notifications', text: this.$t('message.permission_get_system_mobile_notifications') }
    ];
    this.filteredPermissions = this.allPermissions;
  },
  computed: {
    emailRules() {
      return [
        (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || this.$t('message.invalid_email'),
      ];
    },
    visiblePermissions() {
      if (this.selectedPermissions && this.selectedPermissions.root) {
        return this.filteredPermissions.filter(i => i.field === 'root');
      }
      return this.filteredPermissions;
    },
  },
  watch: {
    geolocatedAddress: {
      handler: function (newValue) {
        if (newValue && newValue != '')
          this.updateField("geolocated", newValue);
      },
    },
    localAffiliation: {
      handler: function (newValue) {
        if (
          newValue &&
          newValue != "" &&
          this.academy &&
          this.academy.localAffiliation != newValue
        )
          this.updateField("localAffiliation", newValue);
      },
    },
    affiliation: {
      handler: function (newValue) {
        if (
          newValue &&
          newValue != "" &&
          this.academy &&
          this.academy.affiliation != newValue
        )
          this.updateField("affiliation", newValue);
      },
    },
    selectedAdmin: {
      handler: function (newValue) {
        if (newValue && newValue != "") this.addAdmin(newValue);
      },
    },
    permissionsFilter: {
      handler: function (newValue) {
        this.filterPermissionsDiv(newValue);
      },
    },
  },
  methods: {
    removeAutomatedEmail(type, email) {
      const list = this.academy.automatedEmails[type];
      if (list && list.emails) {
        const index = list.emails.indexOf(email);
        if (index >= 0)
          list.emails.splice(index, 1);
      }
    },
    filterPermissionsDiv(searchText) {
      if (!searchText) {
        this.filteredPermissions = this.allPermissions;
        return;
      }
      const lowerSearch = searchText.toLowerCase();
      this.filteredPermissions = this.allPermissions.filter(item => {
        const fieldMatch = item.field.toLowerCase().includes(lowerSearch);
        const textMatch = item.text?.toString().toLowerCase().includes(lowerSearch);
        return fieldMatch || textMatch;
      });
    },
    addAutomatedEmailTemplate(prop) {
      console.log('PROP: ', prop);
      console.log(this.academy.automatedEmails[prop].template)
    },
    getEmailTemplates() {
      Api2()
        .get(
          "documents/academy/" + this.$store.state.managedAcademy + "/email/templates?archived=false"
        )
        .then((response) => {
          if (response) {
            if (response.data.templates && response.data.templates.length > 0)
              this.emailTemplates = response.data.templates;
          }
        });
    },
    updateContactFormExtraFields() {
      console.log(this.contactFormExtraFields);
      Api()
        .patch("admins/academy/" + this.$store.state.managedAcademy + '/contact-form', { extraFields: this.contactFormExtraFields })
        .then(() => {
          this.showAlert(
            "success",
            this.$t("message.updated")
          );
        })
        .catch(() => {
          this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    removeExtraField(index) {
      this.contactFormExtraFields.splice(index, 1);
    },
    addNewExraField() {
      this.contactFormExtraFields.push({
        fieldName: null,
        required: false,
        fieldType: 'text',
        options: ['', ''],
      });
    },
    addFieldOption(field) {
      if (!field.options) this.$set(field, 'options', []);
      field.options.push('');
    },
    updateAlternateEmails() {
      Api()
        .patch("admins/academy/" + this.$store.state.managedAcademy + '/automated-emails', { automatedEmails: this.academy.automatedEmails })
        .then(() => {
          this.showAlert(
            "success",
            this.$t("message.updated")
          );
        })
        .catch(() => {
          this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    removeAlternativeEmail(prop, val) {
      const index = this.academy.automatedEmails[prop].emails.indexOf(val);
      if (index >= 0) {
        this.academy.automatedEmails[prop].emails.splice(val);
      } else {
        this.showAlert("error", this.$t("message.error_generic"));
      }
    },
    addAlternativeEmail(prop) {
      const email = this.newAlternativeEmails[prop] ? this.newAlternativeEmails[prop].trim().toLowerCase() : null;
      const validEmail = this.isValidEmail(email);
      if (!validEmail) {
        this.showAlert("error", this.$t("message.invalid_email"));
        return;
      }
      const existingEmails = this.academy.automatedEmails[prop].emails;
      if (existingEmails.indexOf(email) >= 0) {
        this.showAlert("error", this.$t("message.duplicate"));
        return;
      }
      this.academy.automatedEmails[prop].emails.push(email);
      this.newAlternativeEmails[prop] = null;
    },
    getAltStripeAccounts() {
      Api2()
        .get(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/altstripeaccounts"
        )
        .then((response) => {
          if (response.data && response.data.accounts)
            this.altStripeAccounts = response.data.accounts;
        });
    },
    connectNewStripeAccount() {
      const url =
        "https://connect.stripe.com/oauth/authorize?" +
        "client_id=" +
        this.stripe_client_id +
        "&state=" +
        this.stripe_state +
        "&scope=read_write&response_type=code" +
        "&stripe_user[email]=" +
        this.newStripe.email;
      window.location = url;
    },
    updateColorTheme() {
      Api()
        .patch("admins/academy/" + this.$store.state.managedAcademy + '/colors', { colorTheme: this.colorTheme })
        .then(() => {
          this.showAlert(
            "success",
            this.$t("message.updated")
          );
          this.colorEditorDialog = false;
        })
        .catch(() => {
          this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    showColorEditor(color) {
      this.colorTheme.currentEditing = color;
      this.colorEditorDialog = true;
    },
    addStripeTerminal() {
      let stripeTerminalScript = document.createElement('script')
      stripeTerminalScript.setAttribute('src', 'https://js.stripe.com/terminal/v1/')
      document.head.appendChild(stripeTerminalScript);
      //this.getAltStripeAccounts();
    },
    pairTerminal(i) {
      this.pairingError = null;
      this.selectedTerminal = i;
      const vm = this;
      this.pairing = true;
      /*eslint-disable no-undef*/
      let terminal = StripeTerminal.create({
        onFetchConnectionToken: function () {
          return Api()
            .post("payments/academy/" + vm.$store.state.managedAcademy + "/terminals/connection_token", { location: i.location })
            .then((response) => {
              return response.data.secret;
            }).catch((e) => {
              if (e)
                vm.showAlert("error", "Can't get pairing token from server");
            });
        },
        onUnexpectedReaderDisconnect: function (e) {
          vm.showAlert("error", vm.$t("message.reader_disconnected"));
          console.log(e.error)
        },
      });
      const config = { simulated: false, location: i.location };
      //const config = { simulated: true };
      //console.log('Discovering devices: ', config);
      terminal.discoverReaders(config).then(function (discoverResult) {
        if (discoverResult.error) {
          vm.pairingError = "Reader is not discoverable";
          vm.pairing = false;
        } else if (discoverResult.discoveredReaders.length === 0) {
          vm.pairingError = vm.$t("message.no_readers_available");
          vm.pairing = false;
        } else {
          //We are only grabbing 1 reader, the id matches the one we should have gotten from the beginnign
          const discoveredReader = discoverResult.discoveredReaders[0];
          //We got a reader, lets connect
          terminal.connectReader(discoveredReader).then(function (connectResult) {
            if (connectResult.error) {
              vm.pairingError = connectResult.error.message;
              vm.pairing = false;
            } else {
              i.paired = true;
              vm.pairing = false;
            }
          });
        }
      });
    },
    getTerminals() {
      Api2()
        .get("payments/academy/" + this.$store.state.managedAcademy + '/terminals')
        .then((response) => {
          if (response.data.locations)
            this.terminals = response.data.locations;
        });
    },
    deleteTerminal(i) {
      Api()
        .delete("payments/academy/" + this.$store.state.managedAcademy + '/terminals/' + i.location)
        .then(() => {
          this.showAlert(
            "success",
            this.$t("message.success")
          );
          this.terminals = [];
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    addTerminal() {
      Api()
        .post("payments/academy/" + this.$store.state.managedAcademy + '/terminals',
          {
            registrationCode: this.registrationCode,
            label: this.readerLabel,
            stripeAccountId: this.readerStripeAccount
          })
        .then((response) => {
          //this.terminals
          this.terminals.push(response.data.terminal);
          this.showAlert(
            "success",
            this.$t("message.success")
          );
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    clearAltImage() {
      this.academy.altImage = null;
      this.altAcademyImage = null;
      this.updateField('altImage', null);
    },
    goToUnsubscribedEmailUser(e) {
      if (e.user)
        this.goToMember(e.user);
    },
    removeUnsubscribedEmail(email) {
      Api()
        .delete("admins/academy/" + this.$store.state.managedAcademy + '/emails/unsubscribed/' + email)
        .then((response) => {
          this.academy.unsubscribedEmails.splice(response.data.index, 1);
          this.showAlert(
            "success",
            this.$t("message.success")
          );
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    addUnsubscribedEmail() {
      Api()
        .patch("admins/academy/" + this.$store.state.managedAcademy + '/emails/unsubscribed', {
          email: this.unsubscribedEmail,
        })
        .then((response) => {
          if (!this.academy.unsubscribedEmails)
            this.academy.unsubscribedEmails = [];
          this.academy.unsubscribedEmails.push(response.data.email);
          this.unsubscribedEmail = null;
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    updateAcademyConfiguration() {
      // These two don't work together - the kiosk checkin flow skips the
      // confirm dialog entirely once class selection is itself the
      // confirmation step - so turning one on always turns the other off,
      // regardless of which switch the user just touched.
      if (this.academy.configuration.forceClassSelectForTabletCheckins) {
        this.academy.configuration.tabletCheckinConfirm = false;
      }
      const data = [
        {
          propName: "configuration",
          value: this.academy.configuration,
        },
      ];
      Api()
        .patch("admins/academy/" + this.$store.state.selectedAcademy, {
          values: data,
        })
        .then((response) => {
          const data = response.data;
          if (data) {
            this.showAlert("success", this.$t("message.updated"));
          }
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    viewAdminPermissions(id) {
      if (this.$store.state.managedAcademy) {
        this.loadingPermissions = true;
        this.selectedPermissions = null;
        Api2()
          .get(
            "admins/academy/" +
            this.$store.state.managedAcademy +
            "/admin/" +
            id +
            "/permissions"
          )
          .then((response) => {
            this.permissionsDialog = true;
            this.selectedPermissions = { adminId: id };
            if (
              response.data.permissions &&
              response.data.permissions.length > 0
            ) {
              if (response && response.data && response.data.permissions) {
                for (const p of response.data.permissions)
                  this.selectedPermissions[p] = true;
              }
            }
            for (const vp of this.validPermissions) {
              var existing = this.selectedPermissions[vp];
              if (!existing) this.selectedPermissions[vp] = false;
            }
            this.loadingPermissions = false;
          });
      } else {
        this.loadingPermissions = false;
        this.showAlert("error", "Please pick your academy again...");
      }
    },
    updateAdminPermissions() {
      // Shallow-clone to force Vue 2 reactivity on dynamically-added properties
      this.selectedPermissions = { ...this.selectedPermissions };
      let permissionsArray = [];
      for (const p in this.selectedPermissions) {
        if (p != "adminId" && this.selectedPermissions[p])
          permissionsArray.push(p);
      }
      if (this.$store.state.managedAcademy) {
        Api().patch(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/admin/" +
          this.selectedPermissions.adminId +
          "/permissions",
          permissionsArray
        );
      } else {
        this.navigateTo({ name: "noacademy" });
      }
    },
    copyPublicUrl() {
      let urlToCopy = 'https://core.bjjlink.com/pub/' + this.academy.identifier;
      this.copyToClipboard(urlToCopy);
    },
    copyAddress() {
      let textToCopy = this.academy.address;
      this.copyToClipboard(textToCopy);
    },
    searchEmail() {
      this.getEmails();
    },
    getEmails(append) {
      if (append) this.loadingMoreEmails = true;
      else this.loadingEmails = true;
      let options = {};
      if (this.selectedEmail)
        options.email = this.selectedEmail;
      if (this.failedEmailsFilter)
        options.failed = true;
      //Cursor is the timestamp of the last row we already have - see the
      //comment in getAcademyEmails for why this is not a skip/offset.
      if (append && this.emails.length)
        options.before = this.emails[this.emails.length - 1].time;

      Api()
        .post("admins/academy/" + this.$store.state.managedAcademy + "/emails", options)
        .then((response) => {
          const batch = response.data.emails || [];
          if (append) {
            //The cursor is inclusive so a shared timestamp is never skipped,
            //which means the first row(s) of a batch can repeat what we already
            //have. Drop those rather than showing the same email twice.
            const rowKey = (e) => (e._id ? "i" + e._id : "g" + e.group + e.time + e.subject);
            const have = {};
            this.emails.forEach((e) => { have[rowKey(e)] = true; });
            const fresh = batch.filter((e) => !have[rowKey(e)]);
            this.emails = this.emails.concat(fresh);
            //If a whole batch was overlap there is nothing further to walk to,
            //so stop rather than leaving a button that does nothing.
            this.emailsHasMore = fresh.length > 0 && response.data.hasMore ? true : false;
          } else {
            this.emails = batch;
            this.emailsHasMore = response.data.hasMore ? true : false;
          }
          this.loadingEmails = false;
          this.loadingMoreEmails = false;
        })
        .catch(() => {
          this.loadingEmails = false;
          this.loadingMoreEmails = false;
        });
    },
    getAvailableEmails() {
      Api2()
        .get("admins/academy/" + this.$store.state.managedAcademy + "/emails/available")
        .then((response) => {
          this.availableEmails = response.data.availableEmails;
        });
    },
    getEmail(email) {
      email.opening = true;
      const id = email._id ? email._id : 'grouped';
      Api2()
        .get(
          "admins/academy/" + this.$store.state.managedAcademy + "/emails/" + id + '?' +
          (email.group ? 'group=' + email.group : '')
        )
        .then((response) => {
          this.email = response.data.email;
          this.emailDialog = true;
          email.opening = false;
        });
    },
    getUpdates() {
      this.updatesSkip = 0;
      const filter = this.activityActionFilter;
      Api2()
        .get(
          "admins/academy/" + this.$store.state.managedAcademy + "/updates?skip=0" +
          (filter ? '&filter=' + filter : '')
        )
        .then((response) => {
          this.updates = response.data.data;
          this.updatesHasMore = response.data.hasMore;
        });
    },
    loadMoreUpdates() {
      this.loadingMoreUpdates = true;
      const nextSkip = this.updatesSkip + 50;
      const filter = this.activityActionFilter;
      Api2()
        .get(
          "admins/academy/" + this.$store.state.managedAcademy + "/updates?skip=" + nextSkip +
          (filter ? '&filter=' + filter : '')
        )
        .then((response) => {
          this.updates = this.updates.concat(response.data.data);
          this.updatesHasMore = response.data.hasMore;
          this.updatesSkip = nextSkip;
          this.loadingMoreUpdates = false;
        })
        .catch(() => {
          this.loadingMoreUpdates = false;
        });
    },
    getAcvitiyActions() {
      if (!this.updates) {
        Api2()
          .get(
            "admins/academy/" + this.$store.state.managedAcademy + "/activities/distinct/actions"
          )
          .then((response) => {
            const tmp = [];
            for (const a of response.data) {
              tmp.push({ text: this.$t("message.admin_action_" + a), value: a })
            }
            this.activityActions = tmp;
          });
      } else {
        this.updates = null;
      }
    },
    addAdmin(id) {
      Api()
        .post(
          "admins/academy/" + this.$store.state.managedAcademy + "/admins",
          { adminId: id }
        )
        .then((response) => {
          const data = response.data;
          if (data) {
            this.managers = data;
            this.selectedAdmin = null;
          }
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    removeAdmin(id) {
      Api()
        .delete(
          "admins/academy/" + this.$store.state.managedAcademy + "/admins/" + id
        )
        .then((response) => {
          const data = response.data;
          if (data) {
            this.managers = data;
          }
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    formatPhone(phone) {
      try {
        return phone
          .replace(/[^0-9]/g, "")
          .replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
      } catch (err) {
        return phone;
      }
    },
    assignCurrentField(field) {
      this.currFieldValue = this.academy[field];
    },
    updateEmailServer() {
      let data = {};
      let proceed = false;
      if (this.hasEmailServer) {
        if (!this.academy.emailServer) {
          this.academy.emailServer = {};
          return;
        }
        if (this.academy.emailServer
          && this.academy.emailServer.password
          && this.academy.emailServer.service
          && this.academy.emailServer.email) {
          data.email = this.academy.emailServer.email;
          data.service = this.academy.emailServer.service;
          data.password = btoa(this.academy.emailServer.password);//Base64 encoded
          proceed = true;
        } else {
          this.showAlert("info", this.$t("message.fill_all_params"));
        }
      } else {
        proceed = true;
      }
      if (proceed) {
        Api()
          .patch("admins/academy/" + this.$store.state.managedAcademy + "/alternative_email_service", data)
          .then(() => {
            this.academy.emailServer = {};
            this.showAlert("success", this.$t("message.updated"));
          })
          .catch((e) => {
            if (e)
              this.showAlert("error", this.$t("message.error_generic"));
          });
      }
    },
    updateTabletCredentials() {
      var value = {
        password: this.academy.tabletCredentials.password,
        id: this.academy.tabletCredentials.id,
      };
      const data = [
        {
          propName: "tabletCredentials",
          value: value,
        },
      ];
      Api()
        .patch("admins/academy/" + this.$store.state.selectedAcademy, {
          values: data,
        })
        .then((response) => {
          const data = response.data;
          if (data) {
            this.updateMessage = this.$t("message.updated");
          }
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    updateField(field, value) {
      this.updateMessage = "";
      if (field === 'email') {
        const emailVal = value !== undefined ? value : this.academy.email;
        if (emailVal && !this.isValidEmail(emailVal)) {
          this.showAlert("error", this.$t("message.invalid_email"));
          return;
        }
      }
      if (
        this.academy[field] != this.currFieldValue ||
        field == "localAffiliation" ||
        field == "affiliation" ||
        field == 'altImage' ||
        field == 'geolocated'
      ) {
        var newValue = this.academy[field];
        if (value) newValue = value;
        if (field == "phone") {
          newValue = this.formatPhone(newValue);
          this.academy.phone = newValue;
        }
        try {
          const data = [
            {
              propName: field,
              value: newValue,
            },
          ];
          Api()
            .patch("admins/academy/" + this.$store.state.selectedAcademy, {
              values: data,
            })
            .then((response) => {
              const data = response.data;
              if (data) {
                this.updateMessage = this.$t("message.updated");
              }
            })
            .catch((e) => {
              if (e) {
                if (e.response.status == 403)
                  this.showAlert(
                    "error",
                    "Oops!",
                    this.$t("message.academy_id_dup")
                  );
                else
                  this.showAlert(
                    "error",
                    "Oops!",
                    this.$t("message.error_generic")
                  );
              }
            });
        } catch (e) {
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        }
      }
    },
  },
};
</script>

<style scoped></style>
